import { computed } from "vue";
import { normalizeJornadas } from "@/composables/admin/dashboard/normalizeJornadas";
import { getSeasonName } from "@/composables/admin/dashboard/seasonUtils";
import { useRacha } from "@/composables/common/useRacha";

/**
 * Datos derivados de jornadas, gráficos, brackets y rachas para el panel de evolución.
 *
 * @param {import('vue').Ref<object|null>} evolutionDataRef — ref con los datos raw de evolución
 * @param {import('vue').ComputedRef<boolean>} isMultiSeasonRef — computed que indica multi-temporada
 * @param {import('vue').ComputedRef<object|null>} currentSeasonEntryRef — computed de la entrada actual
 * @param {import('vue').Ref<Array>} seasonsRef — ref con la lista de temporadas
 * @returns {{
 *     accumulatedChartData: import('vue').ComputedRef<Array>,
 *     chartData: import('vue').ComputedRef<Map<string, Array>>,
 *     seasonBreakdown: import('vue').ComputedRef<Array|null>,
 *     perClubData: import('vue').ComputedRef<{ jornadas: Array, selectedIndex: number }>,
 *     bracketAnalysis: import('vue').ComputedRef<Array|null>,
 *     rachaBadges: import('vue').ComputedRef<Array>,
 * }}
 */
export function useEvolutionJornadas(
    evolutionDataRef,
    isMultiSeasonRef,
    currentSeasonEntryRef,
    seasonsRef,
) {
    const { toBadges } = useRacha();

    /**
     * Calcula datos acumulados desde vs_start (si existen) o desde per_jornada.
     * Esto asegura que funcione para TODAS las temporadas en modo multi.
     */
    function computeAccumulatedByJornada(entry) {
        if (entry?.vs_start?.length) {
            return entry.vs_start.map(j => ({
                label: String(j.jornada),
                revenue: Number(j.accumulated_revenue || 0),
            }));
        }
        if (entry?.per_jornada?.length) {
            const jornadas = normalizeJornadas(entry.per_jornada)
                .sort((a, b) => Number(a.jornada) - Number(b.jornada));
            let acc = 0;
            return jornadas.map(j => {
                acc += Number(j.revenue || 0);
                return { label: String(j.jornada), revenue: acc };
            });
        }
        return [];
    }

    /**
     * Acumula ingresos desde per_jornada ordenado por jornada.
     */
    function accumulateFromJornadas(entry) {
        if (!entry?.per_jornada?.length) return [];
        const sorted = normalizeJornadas(entry.per_jornada)
            .sort((a, b) => Number(a.jornada) - Number(b.jornada));
        let acc = 0;
        return sorted.map(j => {
            acc += Number(j.revenue || 0);
            return { label: String(j.jornada), revenue: acc };
        });
    }

    /**
     * Datos del gráfico de ingresos acumulados.
     * En multi-temporada: todas las temporadas aparecen como series independientes.
     * En individual: una sola serie.
     * Formato Google Charts: [['Jornada', 'Acumulado'], [1, valor], ...]
     *
     * @type {import('vue').ComputedRef<Array>}
     */
    const accumulatedChartData = computed(() => {
        if (!evolutionDataRef.value) return [];
        const perSeason = evolutionDataRef.value?.data?.per_season || {};

        if (isMultiSeasonRef.value) {
            // Construir datos acumulados desde per_jornada para TODAS las temporadas.
            // NO usamos vs_start porque puede no estar disponible para todas.
            const seasonAcc = {};
            const allLabels = new Set();

            // Incluir TODAS las temporadas en seasonAcc aunque no tengan datos,
            // así el chart muestra todas en la leyenda. Las que no tienen jornadas
            // se renderizan como valor 0, consistente con el chart de ingresos/asistencia.
            for (const [id, entry] of Object.entries(perSeason)) {
                const acc = accumulateFromJornadas(entry);
                seasonAcc[id] = acc;
                for (const d of acc) allLabels.add(d.label);
            }

            const seasonEntries = Object.entries(seasonAcc);
            if (seasonEntries.length === 0) return [];

            const sortedLabels = allLabels.size > 0
                ? [...allLabels].sort((a, b) => Number(a) - Number(b))
                : [];

            const header = ["Jornada"];
            for (const [id] of seasonEntries) {
                header.push(getSeasonName(seasonsRef.value, id));
                header.push({ type: 'string', role: 'tooltip' });
            }

            const rows = sortedLabels.map(label => {
                const row = [String(label)];
                for (const [, acc] of seasonEntries) {
                    const found = acc.find(a => a.label === label);
                    const revenue = found ? found.revenue : 0;
                    row.push(revenue);
                    const fmt = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(revenue);
                    row.push(`Jornada ${label}\n${fmt}€`);
                }
                return row;
            });
            if (rows.length === 0) return [];
            return [header, ...rows];
        }

        // Single season: preferir vs_start, fallback a acumular desde per_jornada
        const firstEntry = Object.values(perSeason).find(() => true);
        const acc = computeAccumulatedByJornada(firstEntry);
        if (acc.length === 0) return [];
        const rows = [["Jornada", "Acumulado", { type: 'string', role: 'tooltip' }]];
        for (const d of acc) {
            const fmt = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(d.revenue);
            rows.push([d.label, d.revenue, `Jornada ${d.label}\n${fmt}€`]);
        }
        return rows;
    });

    /**
     * Datos de gráficos normalizados.
     * Genera 4 datasets: revenue_attendance, position_points, racha.
     *
     * @type {import('vue').ComputedRef<Map<string, Array>>}
     */
    const chartData = computed(() => {
        const result = new Map();

        if (!evolutionDataRef.value) return result;

        const perSeason = evolutionDataRef.value?.data?.per_season || {};
        const seasonEntries = Object.entries(perSeason).filter(
            ([, entry]) => entry,
        );

        if (seasonEntries.length === 0) return result;

        if (isMultiSeasonRef.value) {
            const allJornadaLabels = new Set();
            const seasonJornadas = {};

            for (const [seasonId, entry] of seasonEntries) {
                const jornadas = normalizeJornadas(entry.per_jornada);
                seasonJornadas[seasonId] = jornadas;
                for (const j of jornadas) {
                    allJornadaLabels.add(j.jornada);
                }
            }

            const sortedLabels = Array.from(allJornadaLabels).sort();
            if (sortedLabels.length === 0) return result;

            const seasonNames = {};
            for (const [seasonId] of seasonEntries) {
                seasonNames[seasonId] = getSeasonName(seasonsRef.value, seasonId);
            }

            const revAttHeaders = ["Jornada"];
            for (const [id] of seasonEntries) {
                const name = getSeasonName(seasonsRef.value, id);
                revAttHeaders.push(
                    `${name} - Ingresos`,
                    { type: 'string', role: 'tooltip' },
                    `${name} - Asistencia`,
                    { type: 'string', role: 'tooltip' },
                );
            }
            const revAttRows = sortedLabels.map(label => {
                const row = [label];
                for (const [, jornadas] of Object.entries(seasonJornadas)) {
                    const j = jornadas.find(j => j.jornada === label);
                    const revenue = j ? j.revenue : 0;
                    const attendance = j ? j.attendance : 0;
                    const revFmt = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(revenue);
                    const attFmt = new Intl.NumberFormat('es-ES').format(attendance);
                    row.push(
                        revenue,
                        `Jornada ${label}\nIngresos: ${revFmt}€`,
                        attendance,
                        `Jornada ${label}\nAsistencia: ${attFmt}`,
                    );
                }
                return row;
            });
            result.set("revenue_attendance", [revAttHeaders, ...revAttRows]);

            const posHeaders = ["Jornada"];
            for (const [id] of seasonEntries) {
                posHeaders.push(`${seasonNames[id]} (Pos)`);
                posHeaders.push({type: 'string', role: 'tooltip'});
            }
            const posRows = sortedLabels.map(label => {
                const row = [String(label)];
                for (const [, jornadas] of Object.entries(seasonJornadas)) {
                    const j = jornadas.find(j => j.jornada === label);
                    const posicion = j && j.posicion !== null ? j.posicion : 0;
                    row.push(posicion);
                    row.push(`Jornada ${label}\nPosición: ${posicion}º`);
                }
                return row;
            });
            result.set("position_points", [posHeaders, ...posRows]);

            const rachaData = {};
            for (const [seasonId, entry] of seasonEntries) {
                const name = getSeasonName(seasonsRef.value, seasonId);
                rachaData[name] = entry.racha || "";
            }
            result.set("racha", rachaData);
        } else {
            const entry = currentSeasonEntryRef.value;
            if (!entry) return result;

            const jornadas = normalizeJornadas(entry.per_jornada);
            if (jornadas.length === 0) return result;

            const revAttHeaders = ["Jornada", "Ingresos", { type: 'string', role: 'tooltip' }, "Asistencia", { type: 'string', role: 'tooltip' }];
            const revAttRows = jornadas.map(j => {
                const revFmt = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(j.revenue);
                const attFmt = new Intl.NumberFormat('es-ES').format(j.attendance);
                return [
                    j.jornada,
                    j.revenue,
                    `Jornada ${j.jornada}\nIngresos: ${revFmt}€`,
                    j.attendance,
                    `Jornada ${j.jornada}\nAsistencia: ${attFmt}`,
                ];
            });
            result.set("revenue_attendance", [revAttHeaders, ...revAttRows]);

            const posHeaders = ["Jornada", "Posición", {type: 'string', role: 'tooltip'}, "Ingresos", {type: 'string', role: 'tooltip'}];
            const posRows = jornadas.map(j => {
                const revFmt = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(j.revenue);
                return [
                    String(j.jornada),
                    j.posicion,
                    `Jornada ${j.jornada}\nPosición: ${j.posicion}º`,
                    j.revenue,
                    `Jornada ${j.jornada}\n${revFmt}€`,
                ];
            });
            result.set("position_points", [posHeaders, ...posRows]);

            result.set("racha", entry.racha || "");
        }

        return result;
    });

    /**
     * Desglose por temporada para la tabla comparativa.
     * Incluye revenue total, attendance media, posición final, puntos y racha.
     *
     * @type {import('vue').ComputedRef<Array|null>}
     */
    const seasonBreakdown = computed(() => {
        if (!evolutionDataRef.value) return null;
        const perSeason = evolutionDataRef.value?.data?.per_season || {};
        const rows = [];

        for (const [seasonId, entry] of Object.entries(perSeason)) {
            if (!entry) continue;
            const name = getSeasonName(seasonsRef.value, seasonId);
            const jornadas = normalizeJornadas(entry.per_jornada);

            const revenueTotal = jornadas.reduce((sum, j) => sum + j.revenue, 0);
            const attendanceValues = jornadas
                .filter(j => j.attendance > 0)
                .map(j => j.attendance);
            const attendanceAvg =
                attendanceValues.length > 0
                    ? attendanceValues.reduce((sum, v) => sum + v, 0) /
                        attendanceValues.length
                    : 0;

            const lastJornada =
                jornadas.length > 0 ? jornadas[jornadas.length - 1] : null;

            rows.push({
                season: name,
                revenueTotal,
                attendanceAvg: Math.round(attendanceAvg),
                finalPosition: lastJornada?.posicion ?? null,
                points: lastJornada?.puntos ?? 0,
                racha: entry.racha || "",
            });
        }

        return rows.length > 0 ? rows : null;
    });

    /**
     * Datos por club y jornada para la tabla de evolución.
     * Expone jornadas con per-club detalle.
     *
     * @type {import('vue').ComputedRef<{ jornadas: Array, selectedIndex: number }>}
     */
    const perClubData = computed(() => {
        const entry = currentSeasonEntryRef.value;
        if (!entry?.per_jornada?.length) return { jornadas: [], selectedIndex: 0 };
        return {
            jornadas: entry.per_jornada.map(j => ({
                jornada: j.jornada || j.nombre_jornada || "",
                clubs: (() => {
                    if (j.club_name || j.club_id) {
                        return [
                            {
                                clubName: j.club_name || "",
                                shield: j.shield || null,
                                revenue: Number(j.revenue) || 0,
                                attendance: Number(j.attendance) || 0,
                                posicion: j.posicion != null ? Number(j.posicion) : null,
                                puntos: Number(j.puntos) || 0,
                                racha: j.racha || "",
                            },
                        ];
                    }
                    return (j.per_club || []).map(c => ({
                        clubName: c.club_name || c.club?.nombre || "",
                        shield: c.shield || null,
                        revenue: Number(c.revenue) || 0,
                        attendance: Number(c.attendance) || 0,
                        posicion: c.posicion != null ? Number(c.posicion) : null,
                        puntos: Number(c.puntos) || 0,
                        racha: c.racha || "",
                    }));
                })(),
            })),
            selectedIndex: 0,
        };
    });

    /**
     * Análisis por bracket: agrupa clubs de la última jornada en
     * top 3 / medio / bottom 3 con media de ingresos y asistencia.
     *
     * @type {import('vue').ComputedRef<Array|null>}
     */
    const bracketAnalysis = computed(() => {
        const entry = currentSeasonEntryRef.value;
        if (!entry?.per_jornada?.length) return null;
        const lastJornada = entry.per_jornada[entry.per_jornada.length - 1];
        const clubs = (() => {
            if (lastJornada?.club_name || lastJornada?.club_id) {
                return [lastJornada];
            }
            return lastJornada?.per_club || [];
        })();
        if (clubs.length < 3) return null;

        const sorted = [...clubs].sort(
            (a, b) => (a.posicion || 999) - (b.posicion || 999),
        );
        const total = sorted.length;

        // Distribución dinámica: nTop y nBottom no se solapan
        const nTop = Math.min(3, total);
        const nBottom = Math.min(3, Math.max(0, total - nTop));

        const brackets = [
            { label: "Los 3 primeros", clubs: sorted.slice(0, nTop) },
            { label: "Medio", clubs: sorted.slice(nTop, total - nBottom) },
            { label: "Últimos 3", clubs: sorted.slice(total - nBottom) },
        ];

        return brackets.map(b => {
            const avgRevenue = b.clubs.length
                ? b.clubs.reduce((s, c) => s + (Number(c.revenue) || 0), 0) /
                    b.clubs.length
                : 0;
            const avgAttendance = b.clubs.length
                ? b.clubs.reduce((s, c) => s + (Number(c.attendance) || 0), 0) /
                    b.clubs.length
                : 0;
            return {
                label: b.label,
                count: b.clubs.length,
                avgRevenue: Math.round(avgRevenue),
                avgAttendance: Math.round(avgAttendance),
                clubs: b.clubs.map(c => ({
                    name: c.club_name || c.club?.nombre || "",
                    posicion: c.posicion,
                    revenue: Number(c.revenue) || 0,
                    attendance: Number(c.attendance) || 0,
                })),
            };
        });
    });

    /**
     * Racha badges para vista individual (single-season).
     *
     * @type {import('vue').ComputedRef<Array>}
     */
    const rachaBadges = computed(() => {
        const perSeason = evolutionDataRef.value?.data?.per_season || {};
        const firstSeason = Object.values(perSeason)[0];
        if (!firstSeason?.per_jornada?.length) return [];
        const lastJornada =
            firstSeason.per_jornada[firstSeason.per_jornada.length - 1];
        const rachaStr = lastJornada?.per_club?.[0]?.racha || lastJornada?.racha;
        if (!rachaStr) return [];
        return toBadges(rachaStr);
    });

    return {
        accumulatedChartData,
        chartData,
        seasonBreakdown,
        perClubData,
        bracketAnalysis,
        rachaBadges,
    };
}
